/**
 * API client for ISO Portal
 */

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_BASE_URL = BACKEND_BASE.includes('/api') ? BACKEND_BASE : `${BACKEND_BASE}/api`;

class APIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this._refreshPromise = null;
  }

  getHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async _refreshAccessToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) throw new Error('No refresh token');

    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) throw new Error('Refresh failed');
    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    return data.access_token;
  }

  async request(endpoint, options = {}, _isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers
      }
    };
    if (typeof FormData !== 'undefined' && options.body instanceof FormData) delete config.headers['Content-Type'];

    try {
      const response = await fetch(url, config);
      
      // Read body immediately to avoid "body already used" issues
      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = { detail: responseText || `HTTP ${response.status}` };
      }
      
      // If 401 and we have a refresh token, try refreshing once
      if (response.status === 401 && !_isRetry && localStorage.getItem('refresh_token')) {
        try {
          // Deduplicate concurrent refresh calls
          if (!this._refreshPromise) {
            this._refreshPromise = this._refreshAccessToken();
          }
          await this._refreshPromise;
          this._refreshPromise = null;
          return this.request(endpoint, options, true);
        } catch {
          this._refreshPromise = null;
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (!response.ok) {
        const detail = responseData.detail;
        // For structured errors (like lockout), stringify the detail object
        if (typeof detail === 'object') {
          const err = new Error(JSON.stringify(detail));
          err.status = response.status;
          err.lockoutData = detail; // Also attach the parsed data
          throw err;
        }
        const err = new Error(detail || `HTTP ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return responseData;
    } catch (error) {
      if (!options.quiet) console.error('API Error:', error);
      throw error;
    }
  }

  // Auth
  async requestDemoAccess(data) {
    return this.request('/auth/demo-request', { method: 'POST', body: JSON.stringify(data) });
  }

  async verifyDemoToken(token) {
    return this.request(`/auth/demo-verify/${token}`);
  }

  async getDemoLeads() {
    return this.request('/admin/demo-leads');
  }

  async demoLogin() {
    return this.request('/auth/demo-login', { method: 'POST' });
  }

  async getSetupProgress() {
    return this.request('/tenant/setup-progress');
  }

  async dismissSetupProgress() {
    return this.request('/tenant/setup-progress/dismiss', { method: 'POST' });
  }

  async login(email, password) {
    // Use XMLHttpRequest for login to properly handle lockout responses
    // This avoids the "body stream already read" issue with fetch
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseURL}/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            const detail = data.detail;
            if (typeof detail === 'object') {
              const err = new Error(JSON.stringify(detail));
              err.status = xhr.status;
              err.lockoutData = detail;
              reject(err);
            } else {
              const err = new Error(detail || `HTTP ${xhr.status}`);
              err.status = xhr.status;
              reject(err);
            }
          }
        } catch (e) {
          const err = new Error(`HTTP ${xhr.status}`);
          err.status = xhr.status;
          reject(err);
        }
      };
      
      xhr.onerror = () => {
        reject(new Error('Network error'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  // Leads
  async getLeads(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/leads${params ? `?${params}` : ''}`);
  }

  async getLead(id) {
    return this.request(`/leads/${id}`);
  }

  async createLead(leadData) {
    return this.request('/leads', {
      method: 'POST',
      body: JSON.stringify(leadData)
    });
  }

  async updateLead(id, updates) {
    return this.request(`/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async upload(path, formData) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseURL}${path}`, {
      method: 'POST', headers: { ...(token && { Authorization: `Bearer ${token}` }) }, body: formData,
    });
    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  }

  // Lead Documents
  async uploadLeadDocument(leadId, file, documentType) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    const token = localStorage.getItem('token');
    const response = await fetch(
      `${this.baseURL}/leads/${leadId}/documents/upload`,
      {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  async getLeadDocuments(leadId) {
    return this.request(`/leads/${leadId}/documents`);
  }

  // Applications
  async getApplications(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/applications${params ? `?${params}` : ''}`);
  }

  async getApplication(id) {
    return this.request(`/applications/${id}`);
  }

  async createApplication(leadId) {
    return this.request('/applications', {
      method: 'POST',
      body: JSON.stringify({ lead_id: leadId })
    });
  }

  async updateApplication(id, stepNumber, stepData) {
    return this.request(`/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ step_number: stepNumber, step_data: stepData })
    });
  }

  async submitApplication(id) {
    return this.request(`/applications/${id}/submit`, {
      method: 'POST'
    });
  }

  // Documents
  async uploadDocument(appId, file, documentType) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    const token = localStorage.getItem('token');
    const response = await fetch(
      `${this.baseURL}/applications/${appId}/documents/upload`,
      {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  async getDocuments(appId) {
    return this.request(`/applications/${appId}/documents`);
  }

  async approveOCR(docId) {
    return this.request(`/documents/${docId}/approve-ocr`, {
      method: 'POST'
    });
  }

  // Dashboard
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getRecentActivity(limit = 10) {
    return this.request(`/dashboard/recent-activity?limit=${limit}`);
  }

  // ── Super Admin APIs ──────────────────────────────────────

  async getSuperAdminDashboard() {
    return this.request('/super-admin/dashboard');
  }

  // Tenants
  async getTenants() {
    return this.request('/super-admin/tenants');
  }

  async getTenant(id) {
    return this.request(`/super-admin/tenants/${id}`);
  }

  async createTenant(data) {
    return this.request('/super-admin/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTenant(id, data) {
    return this.request(`/super-admin/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async suspendTenant(id) {
    return this.request(`/super-admin/tenants/${id}/suspend`, { method: 'POST' });
  }

  async activateTenant(id) {
    return this.request(`/super-admin/tenants/${id}/activate`, { method: 'POST' });
  }

  async archiveTenant(id) {
    return this.request(`/super-admin/tenants/${id}/archive`, { method: 'POST' });
  }

  // Users (Super Admin)
  async getAdminUsers(tenantId) {
    const params = tenantId ? `?tenant_id=${tenantId}` : '';
    return this.request(`/super-admin/users${params}`);
  }

  async createAdminUser(data) {
    return this.request('/super-admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async inviteAdminUser(data) {
    return this.request('/super-admin/users/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminUser(id, data) {
    return this.request(`/super-admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async suspendUser(id) {
    return this.request(`/super-admin/users/${id}/suspend`, { method: 'POST' });
  }

  async deactivateUser(id) {
    return this.request(`/super-admin/users/${id}/deactivate`, { method: 'POST' });
  }

  async resetUserPassword(id) {
    return this.request(`/super-admin/users/${id}/reset-password`, { method: 'POST' });
  }

  async resendUserInvite(id) {
    return this.request(`/super-admin/users/${id}/resend-invite`, { method: 'POST' });
  }

  async impersonateUser(id) {
    return this.request(`/super-admin/users/${id}/impersonate`, { method: 'POST' });
  }

  // Audit Logs
  async getAuditLogs(params = {}) {
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return this.request(`/super-admin/audit-logs${query ? `?${query}` : ''}`);
  }

  // ── Tenant-Scoped APIs (ISO Admin) ────────────────────────

  async getTenantUsers() {
    return this.request('/tenant/users');
  }

  async createTenantUser(data) {
    return this.request('/tenant/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async inviteTenantUser(data) {
    return this.request('/tenant/users/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTenantSettings(section) {
    return this.request(`/tenant/settings/${section}`);
  }

  async getTenantSettingsAll() {
    return this.request('/tenant/settings');
  }

  async updateTenantSettings(section, settings) {
    return this.request(`/tenant/settings/${section}`, {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  }

  async updateTenantBranding(settings) {
    return this.request('/tenant/settings/branding', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  }

  // ── Payment / Merchant APIs ───────────────────────────────

  async getMyMerchantAccount() {
    return this.request('/payments/merchant-account');
  }

  async applyMerchantAccount() {
    return this.request('/payments/merchant-account/apply', { method: 'POST' });
  }

  async getMerchantAccounts(status) {
    const q = status ? `?status=${status}` : '';
    return this.request(`/payments/admin/merchant-accounts${q}`);
  }

  async activateMerchantAccount(accountId, varSheet) {
    return this.request(`/payments/admin/merchant-accounts/${accountId}/activate`, {
      method: 'POST',
      body: JSON.stringify(varSheet),
    });
  }

  async rejectMerchantAccount(accountId) {
    return this.request(`/payments/admin/merchant-accounts/${accountId}/reject`, { method: 'POST' });
  }

  async updateVarSheet(accountId, varSheet) {
    return this.request(`/payments/admin/merchant-accounts/${accountId}/var-sheet`, {
      method: 'PUT',
      body: JSON.stringify(varSheet),
    });
  }

  async getPaymentProfiles(type) {
    const q = type ? `?profile_type=${type}` : '';
    return this.request(`/payments/profiles${q}`);
  }

  async createPaymentProfile(data) {
    return this.request('/payments/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePaymentProfile(id, data) {
    return this.request(`/payments/profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePaymentProfile(id) {
    return this.request(`/payments/profiles/${id}`, { method: 'DELETE' });
  }

  async getTransactions(type, limit = 50, skip = 0) {
    const params = new URLSearchParams({ limit, skip });
    if (type) params.set('payment_type', type);
    return this.request(`/payments/transactions?${params}`);
  }

  async createTransaction(data) {
    return this.request('/payments/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── Lead Forms Builder ───────────────────────────────────
  async getLeadForms() {
    return this.request('/lead-forms');
  }

  async getLeadForm(formId) {
    return this.request(`/lead-forms/${formId}`);
  }

  async createLeadForm(data) {
    return this.request('/lead-forms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLeadForm(formId, data) {
    return this.request(`/lead-forms/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLeadForm(formId) {
    return this.request(`/lead-forms/${formId}`, { method: 'DELETE' });
  }

  async getFormSubmissions(formId, limit = 50, skip = 0) {
    const params = new URLSearchParams({ limit, skip });
    if (formId) params.set('form_id', formId);
    return this.request(`/lead-forms/submissions/list?${params}`);
  }

  async getWebhookDeliveries(formId) {
    const params = formId ? `?form_id=${formId}` : '';
    return this.request(`/lead-forms/webhooks/deliveries${params}`);
  }

  // ── Affiliates ───────────────────────────────────────────
  async getAffiliates() {
    return this.request('/affiliates');
  }

  async createAffiliate(data) {
    return this.request('/affiliates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAffiliate(id, data) {
    return this.request(`/affiliates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAffiliateStats(id) {
    return this.request(`/affiliates/${id}/stats`);
  }

  // ── Products & Routing ───────────────────────────────────
  async getProducts() {
    return this.request('/products');
  }

  async updateProduct(name, data) {
    return this.request(`/products/${name}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getHardware() {
    return this.request('/hardware');
  }

  async routeProduct(monthlyVolume, productHint) {
    return this.request('/products/route', {
      method: 'POST',
      body: JSON.stringify({ monthly_volume: monthlyVolume, product_hint: productHint }),
    });
  }

  // ── Commissions ──────────────────────────────────────────
  async getCommissions(affiliateId, status) {
    const params = new URLSearchParams();
    if (affiliateId) params.set('affiliate_id', affiliateId);
    if (status) params.set('status', status);
    return this.request(`/commissions?${params}`);
  }

  // ── Lead Events ──────────────────────────────────────────
  async getLeadEvents(leadId) {
    return this.request(`/leads/${leadId}/events`);
  }

  // ── GHL Integration ──────────────────────────────────────
  async getGHLStatus() {
    return this.request('/integrations/ghl/status');
  }

  async configureGHL(apiKey, locationId, enabled) {
    return this.request('/integrations/ghl/configure', {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey, location_id: locationId, enabled }),
    });
  }

  // ── Conversion Funnel ────────────────────────────────────
  async getFunnelStats(affiliateId, days = 30) {
    const params = new URLSearchParams({ days });
    if (affiliateId) params.set('affiliate_id', affiliateId);
    return this.request(`/funnel/stats?${params}`);
  }

  // ── Sales GPS ─────────────────────────────────────────────
  async getSalesGPSDashboard(userId) {
    const qs = userId ? `?user_id=${userId}` : '';
    return this.request(`/sales-gps/dashboard${qs}`);
  }

  async getSalesGPSGoal(userId) {
    const qs = userId ? `?user_id=${userId}` : '';
    return this.request(`/sales-gps/goal${qs}`);
  }

  async setSalesGPSGoal(payload) {
    return this.request('/sales-gps/goal', { method: 'PUT', body: JSON.stringify(payload) });
  }

  async getSalesGPSBaselineRates() {
    return this.request('/sales-gps/baseline-rates');
  }

  async updateSalesGPSBaselineRates(payload) {
    return this.request('/sales-gps/baseline-rates', { method: 'PUT', body: JSON.stringify(payload) });
  }

  async getSalesGPSTeamSummary() {
    return this.request('/sales-gps/team-summary');
  }

  async getSalesGPSNext20() {
    return this.request('/sales-gps/next-20');
  }

  async getSalesGPSAlerts() {
    return this.request('/sales-gps/alerts');
  }

  // ── Tasks ─────────────────────────────────────────────────
  async getMyDay() {
    return this.request('/tasks/views/my-day');
  }

  async getTasks(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/tasks${qs ? `?${qs}` : ''}`);
  }

  async createTask(data) {
    return this.request('/tasks', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTask(id, data) {
    return this.request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async completeTask(id, data = {}) {
    return this.request(`/tasks/${id}/complete`, { method: 'POST', body: JSON.stringify(data) });
  }

  async snoozeTask(id, data) {
    return this.request(`/tasks/${id}/snooze`, { method: 'POST', body: JSON.stringify(data) });
  }

  async cancelTask(id) {
    return this.request(`/tasks/${id}`, { method: 'DELETE' });
  }

  async getNeglectedLeads() {
    return this.request('/tasks/views/neglected');
  }

  async getManagerTaskStats() {
    return this.request('/tasks/stats/manager');
  }

  async getTaskTypes() {
    return this.request('/tasks/meta/types');
  }

  // ── Weekly Planner ────────────────────────────────────────
  async getPlannerConfig() {
    return this.request('/planner/config');
  }

  async getPlannerWeek(startDate) {
    const qs = startDate ? `?start_date=${startDate}` : '';
    return this.request(`/planner/week${qs}`);
  }

  async createPlannerBlock(data) {
    return this.request('/planner/blocks', { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePlannerBlock(id, data) {
    return this.request(`/planner/blocks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deletePlannerBlock(id) {
    return this.request(`/planner/blocks/${id}`, { method: 'DELETE' });
  }

  async assignTaskToBlock(blockId, taskId) {
    return this.request(`/planner/blocks/${blockId}/tasks/${taskId}`, { method: 'POST' });
  }

  async unassignTaskFromBlock(blockId, taskId) {
    return this.request(`/planner/blocks/${blockId}/tasks/${taskId}`, { method: 'DELETE' });
  }

  async quickPlaceTask(taskId, data) {
    return this.request(`/planner/tasks/${taskId}/quick-place`, { method: 'POST', body: JSON.stringify(data) });
  }

  async buildMyWeek(startDate) {
    return this.request('/planner/build-week', { method: 'POST', body: JSON.stringify({ start_date: startDate }) });
  }

  // ── KYC / Compliance ──────────────────────────────────────
  async getKycChecklistForLead(leadId) {
    return this.request(`/kyc/checklists/for-lead/${leadId}`);
  }

  async getKycChecklistForApplication(applicationId) {
    return this.request(`/kyc/checklists/for-application/${applicationId}`);
  }

  async submitKycItem(checklistId, slot, data = {}) {
    return this.request(`/kyc/checklists/${checklistId}/items/${slot}/submit`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async approveKycItem(checklistId, slot) {
    return this.request(`/kyc/checklists/${checklistId}/items/${slot}/approve`, { method: 'PUT' });
  }

  async rejectKycItem(checklistId, slot, reason) {
    return this.request(`/kyc/checklists/${checklistId}/items/${slot}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) });
  }

  async addKycMissingToTodo(checklistId, data) {
    return this.request(`/kyc/checklists/${checklistId}/add-missing-to-todo`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getKycQueue() {
    return this.request('/kyc/queue');
  }

  // ── Weekly Digest ─────────────────────────────────────────
  async getWeeklyDigest() {
    return this.request('/digest/weekly');
  }

  async getDigestStatus() {
    return this.request('/digest/status');
  }

  async dismissDigest() {
    return this.request('/digest/dismiss', { method: 'POST' });
  }

  async sendDigestNow() {
    return this.request('/digest/send', { method: 'POST' });
  }

  async addMissedToTodo(data) {
    return this.request('/digest/add-missed-to-todo', { method: 'POST', body: JSON.stringify(data) });
  }

  // ── Schedule A (Compensation Engine) ────────────────────────────
  async getScheduleAMyView(period) {
    const q = period ? `?period=${period}` : '';
    return this.request(`/schedule-a/my-view${q}`);
  }
  async getScheduleADownlineManager() {
    return this.request('/schedule-a/downline-manager');
  }
  async getScheduleAPendingApprovals() {
    return this.request('/schedule-a/pending-approvals');
  }
  async getScheduleARelationships(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/schedule-a/relationships${q ? `?${q}` : ''}`);
  }
  async createScheduleARelationship(body) {
    return this.request('/schedule-a/relationships', { method: 'POST', body: JSON.stringify(body) });
  }
  async proposeScheduleAChange(relId, body) {
    return this.request(`/schedule-a/relationships/${relId}/propose-change`, { method: 'POST', body: JSON.stringify(body) });
  }
  async approveScheduleA(relId) {
    return this.request(`/schedule-a/relationships/${relId}/approve`, { method: 'POST' });
  }
  async rejectScheduleA(relId) {
    return this.request(`/schedule-a/relationships/${relId}/reject`, { method: 'POST' });
  }
  async getProcessorCostTemplates(processorSlug) {
    const q = processorSlug ? `?processor_slug=${processorSlug}` : '';
    return this.request(`/schedule-a/processor-cost-templates${q}`);
  }
  async createProcessorCostTemplate(body) {
    return this.request('/schedule-a/processor-cost-templates', { method: 'POST', body: JSON.stringify(body) });
  }
  async getIsoCostTemplates(processorSlug) {
    const q = processorSlug ? `?processor_slug=${processorSlug}` : '';
    return this.request(`/schedule-a/iso-cost-templates${q}`);
  }
  async createIsoCostTemplate(body) {
    return this.request('/schedule-a/iso-cost-templates', { method: 'POST', body: JSON.stringify(body) });
  }
  async assignIsoCostTemplate(body) {
    return this.request('/schedule-a/iso-cost-templates/assign', { method: 'POST', body: JSON.stringify(body) });
  }
  async getScheduleAWaterfall(period, tenantId, mid) {
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (mid) params.merchant_mid = mid;
    const q = new URLSearchParams(params).toString();
    return this.request(`/schedule-a/waterfall/${period}${q ? `?${q}` : ''}`);
  }
  async recalculateSchedulePeriod(period, tenantId) {
    const q = tenantId ? `?period=${period}&tenant_id=${tenantId}` : `?period=${period}`;
    return this.request(`/schedule-a/recalculate${q}`, { method: 'POST' });
  }
  async getShadowReconciliation(period, tenantId) {
    const params = { period };
    if (tenantId) params.tenant_id = tenantId;
    return this.request(`/schedule-a/shadow-reconciliation?${new URLSearchParams(params)}`);
  }

  // ── Team Onboarding + Custom Menu Links + Permissions ────────────
  async getFeatureList() {
    return this.request('/team/permissions/feature-list');
  }
  async getMyEffectivePermissions() {
    return this.request('/team/permissions/mine');
  }
  async getUserPermissions(userId) {
    return this.request(`/team/permissions/${userId}`);
  }
  async setUserPermissions(userId, overrides) {
    return this.request(`/team/permissions/${userId}`, { method: 'PUT', body: JSON.stringify({ overrides }) });
  }
  async getTeamProfile(userId) {
    return this.request(`/team/profile/${userId}`);
  }
  async updateTeamProfile(userId, body) {
    return this.request(`/team/profile/${userId}`, { method: 'PUT', body: JSON.stringify(body) });
  }
  async unmaskPii(userId, body) {
    return this.request(`/team/profile/${userId}/unmask`, { method: 'POST', body: JSON.stringify(body) });
  }
  async wizardCreateUser(body) {
    return this.request('/team/wizard', { method: 'POST', body: JSON.stringify(body) });
  }
  async listMenuLinks() {
    return this.request('/menu-links');
  }
  async getMyMenuLinks() {
    return this.request('/menu-links/for-me');
  }
  async createMenuLink(body) {
    return this.request('/menu-links', { method: 'POST', body: JSON.stringify(body) });
  }
  async updateMenuLink(id, body) {
    return this.request(`/menu-links/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }
  async deleteMenuLink(id) {
    return this.request(`/menu-links/${id}`, { method: 'DELETE' });
  }

  // ── Universal Customer Identity (duplicate review) ──
  async listIdentityCandidates(limit = 200, sort = 'confidence', includeDeferred = false) {
    return this.request(
      `/identity/candidates?limit=${limit}&sort=${sort}&include_deferred=${includeDeferred}`
    );
  }
  async listIdentityMerges(limit = 100) {
    return this.request(`/identity/merges?limit=${limit}`);
  }
  async decideIdentityCandidate(candidateId, action, notes) {
    return this.request(`/identity/candidates/${candidateId}/${action}`, {
      method: 'POST',
      body: JSON.stringify({ notes: notes || null }),
    });
  }
  async getJobStats() {
    return this.request('/jobs/stats');
  }
  async getDeadLetterQueue() {
    return this.request('/jobs/dead-letter');
  }
  async runDueJobs() {
    return this.request('/jobs/run-now', { method: 'POST' });
  }
  async requeueJob(jobId) {
    return this.request(`/jobs/${jobId}/requeue`, { method: 'POST' });
  }
  async getEventFailures() {
    return this.request('/events/failures');
  }
  async retryFailedEventDeliveries() {
    return this.request('/events/retry-failed', { method: 'POST' });
  }
  async getFieldPolicy() {
    return this.request('/security/field-policy');
  }
  async revealField(payload) {
    return this.request('/security/reveal-field', { method: 'POST', body: JSON.stringify(payload) });
  }
  async getProvenance(entityType, entityId, includeHistory = false) {
    return this.request(`/provenance/${entityType}/${entityId}?include_history=${includeHistory}`);
  }
  async getMidHealth(statusFilter = '') {
    const qs = statusFilter ? `?status_filter=${encodeURIComponent(statusFilter)}` : '';
    return this.request(`/merchants/mid-health${qs}`);
  }
  async listBusinessEvents(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/events${qs ? `?${qs}` : ''}`);
  }
  async getBusinessContext(businessId) {
    return this.request(`/identity/businesses/${businessId}/context`, { quiet: true });
  }
  async getBusinessTimeline(businessId, limit = 300) {
    return this.request(`/identity/businesses/${businessId}/timeline?limit=${limit}`, { quiet: true });
  }
  async bulkDecideIdentityCandidates(candidateIds, decision, notes) {
    return this.request('/identity/candidates/bulk', {
      method: 'POST',
      body: JSON.stringify({ candidate_ids: candidateIds, decision, notes: notes || null }),
    });
  }
  async reverseIdentityMerge(mergeEventId) {
    return this.request(`/identity/merges/${mergeEventId}/reverse`, { method: 'POST' });
  }
}

export default new APIClient();
